import { useEffect, useState } from "react";
import type { Client } from "../../data/schemaTypes";
import { useMastersStore } from "../../data/mastersStore";
import { Button, Input, Select } from "../../components/ui";

type Props = {
  client: Client | null;
  onClose: () => void;
};

// Editor de campos base del cliente. La gestión de direcciones (1-N) se
// hará desde el detail drawer en una iteración futura — por ahora solo se
// edita la cabecera (denormalizada en `clients`).
export function ClientEditModal({ client, onClose }: Props) {
  const updateClient = useMastersStore((s) => s.updateClient);
  const [name, setName] = useState("");
  const [taxIdType, setTaxIdType] = useState<"RUC" | "DNI" | "CE">("RUC");
  const [taxId, setTaxId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!client) return;
    setName(client.name);
    setTaxIdType((client.tax_id_type as "RUC" | "DNI" | "CE") ?? "RUC");
    setTaxId(client.tax_id ?? "");
    setContactName(client.contact_name ?? "");
    setContactPhone(client.contact_phone ?? "");
    setEmail(client.email ?? "");
    setBusinessType(client.business_type ?? "");
    setActive(client.active);
  }, [client]);

  if (!client) return null;

  function save() {
    if (!client) return;
    updateClient(client.id, {
      name: name.trim(),
      tax_id_type: taxIdType,
      tax_id: taxId.trim() || undefined,
      contact_name: contactName.trim() || undefined,
      contact_phone: contactPhone.trim() || undefined,
      email: email.trim() || undefined,
      business_type: businessType.trim() || undefined,
      active,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[2100] bg-ink/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-card-hover w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
          <h2 className="text-base font-semibold text-ink">
            Editar cliente · {client.code}
          </h2>
          <button onClick={onClose} className="text-sub hover:text-ink text-xs">
            cerrar ✕
          </button>
        </div>

        <div className="space-y-3">
          <Input
            label="Nombre / razón social"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <Select
              label="Tipo de doc."
              value={taxIdType}
              onChange={(e) =>
                setTaxIdType(e.target.value as "RUC" | "DNI" | "CE")
              }
            >
              <option value="RUC">RUC</option>
              <option value="DNI">DNI</option>
              <option value="CE">CE</option>
            </Select>
            <Input
              label="Número"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nombre de contacto"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
            <Input
              label="Teléfono"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Tipo de negocio"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            placeholder="bodega, minimarket, restaurante…"
          />
          <label className="flex items-center gap-2 text-sm text-ink mt-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="accent-primary"
            />
            <span>Activo</span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={save}>
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}
